import React from "react";
import { JSONGeometricShape } from "../../Palettes/Headerbar/toolbarButtonOpen";
import { KonvaEventObject } from "konva/lib/Node";
import { Segment } from "../shapes/line/Line";
import { PaletteRefProps } from "../shapes/line/LineFloatingPalette";

interface ToolRenderingFunctions {
  toolRender: () => React.JSX.Element;
}
export interface ShapeDataTransformer {
  snapPointCalculator: (s: GeometricShape) => GeometricShape
  rectangleIntersectCheck: (s: GeometricShape, r: RectSel) => boolean,
  globalShapeTransformer: (s: GeometricShape) => ShapeJSON,
  transformToInternalShape: (s: JSONGeometricShape) => GeometricShape,
  calculateWidthHeight: (s: GeometricShape) => {
    width: number,
    height: number,
    boundingboxPosition: Point2d,
  }
  shapeSplit?: (s: GeometricShape, l: Segment) => GeometricShape[]
}
export interface RenderingFunctions {
  shapeRender: ((shapeProps: GeometricShape, onSelect: (e: KonvaEventObject<MouseEvent>) => void,) => React.JSX.Element),
  baseShapeRenderer?: ((shapeProps: GeometricShape) => React.JSX.Element)
  rubberlineRender: ((shapeProps: GeometricShape, onSelect: (e: KonvaEventObject<MouseEvent>) => void,) => React.JSX.Element),
}
/**
 * Type of whatever we want to store in the singleton
 * In this case, it is an object with a name attribute
 */
export interface ShapeFunctions {
  shapeRendererGroup: RenderingFunctions,
  shapeTransformerGroup: ShapeDataTransformer,
  UIElementRender: UIElementRender,
}
export interface UIElementRender {
  propertyPaletteRenderer: (shape: GeometricShape) => React.JSX.Element,
  defaultPropertyPaletteRenderer: (shape: GeometricShape) => React.JSX.Element,
  floatingPalette?: (ref: React.RefObject<PaletteRefProps>) => React.JSX.Element,
}

interface ToolRendererGroup {
  toolRendering: ToolRenderingFunctions,
};

export type SingletonValue = {
  rendererGroups: Map<string, ShapeFunctions>,
  toolRendererGroup: Map<string, ToolRendererGroup>,
};

/**
 * The only instance of our Singleton
 */
let instance: ReturnType<typeof makeSingleton<SingletonValue>>;

/**
 * Singleton supplies accessors using Revealing Module
 * pattern and we use generics, since we could reuse
 * this across multiple singletons
 *
 * Note: Object.freeze() not required due to type narrowing!
 */
const makeSingleton = <T>(initial?: T) => {
  /** Closure of the singleton's value to keep it private */
  let _value: T | undefined = initial;

  /** Only the accessors are returned */
  return {
    getValue: (): T | undefined => _value,
    setValue: (value: T) => _value = value,
  };
};

/**
 * Retrieves the only instance of the Singleton
 * and allows a once-only initialisation
 * (additional changes require the setValue accessor)
 */
const getInstance = (initial?: SingletonValue) => {
  if (!instance) {
    instance = makeSingleton<SingletonValue>(initial);
    return instance;
  }
  if (initial) {
    throw Error("Singleton already initialised")
  }
  return instance;
};

// Shape render method
const getShapeRendererGroup = (name: string): RenderingFunctions | undefined => {
  return getInstance().getValue()?.rendererGroups?.get(name)?.shapeRendererGroup;
}
const getToolRenderer = (name: ToolType): ToolRenderingFunctions | undefined => {
  return getInstance().getValue()?.toolRendererGroup?.get(name)?.toolRendering;
}
const getShapeTransfomer = (name: string): ShapeDataTransformer | undefined => {
  return getInstance().getValue()?.rendererGroups?.get(name)?.shapeTransformerGroup;
}
const getGlobalTransformer = (name: string) => {
  return getInstance().getValue()?.rendererGroups?.get(name)?.shapeTransformerGroup.globalShapeTransformer;
}

// Propertypanel render method
const getPropertyPaletteRenderer = (name: string) => {
  return getInstance().getValue()?.rendererGroups.get(name)?.UIElementRender.propertyPaletteRenderer;
}

// Default Settings render method
const getDefaultPropertyPaletteRenderer = (name: string) => {
  return getInstance().getValue()?.rendererGroups.get(name)?.UIElementRender.defaultPropertyPaletteRenderer;
}
const getFloatingPalette = (name: string) => {
  return getInstance().getValue()?.rendererGroups.get(name)?.UIElementRender?.floatingPalette;
}

const initialize = () => {
  if (getInstance().getValue() === undefined) {
    getInstance().setValue(
      {
        rendererGroups: new Map<string, ShapeFunctions>(),
        toolRendererGroup: new Map<string, ToolRendererGroup>()
      })
  }
}

const registerToolRendererGroup = (name: string, toolRendering: ToolRenderingFunctions) => {
  initialize();
  getInstance().getValue()?.toolRendererGroup.set(name, { toolRendering });
}


const registerRendererGroup = (name: string, shapeFunctions: ShapeFunctions) => {
  initialize();
  getInstance().getValue()?.rendererGroups.set(
    name,
    shapeFunctions);
}

export {
  registerToolRendererGroup,
  registerRendererGroup,
  getShapeRendererGroup,
  getPropertyPaletteRenderer,
  getDefaultPropertyPaletteRenderer,
  getToolRenderer,
  getGlobalTransformer,
  getShapeTransfomer,
  getFloatingPalette,
};